/**
 * Level 2 — Pipe Dreams.
 *
 * A depth-first maze on a one-cell lattice, biased hard toward carrying
 * straight on. That bias is the whole trick: it produces long service runs that
 * feel like they are going somewhere, joined by junctions that all look the
 * same, ending in dead ends that all look the same. A handful of chambers and
 * two trunk corridors are the only landmarks in the entire level.
 */

import { Cell } from '../../core/types';
import { scatterDamp } from './decorate';
import { addRegion, fillRect, interiorRect, type GenSite, type Rect } from './site';
import { applyWrongness } from './wrongness';

/** Chance the walker carries straight on when it can. High makes long runs. */
const STRAIGHT_BIAS = 0.66;
/** Fraction of dead ends that get a second opening, turning them into junctions. */
const BRAID = 0.18;

const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];

/** Carve the Pipe Dreams profile into `site`. */
export function generatePipes(site: GenSite): void {
  const { grid, rng } = site;
  const bounds = interiorRect(grid);
  // Nodes sit on odd cells so the wall between two nodes is exactly one cell.
  const nx = (bounds.w + 1) >> 1;
  const ny = (bounds.h + 1) >> 1;
  const cellX = (i: number): number => bounds.x + i * 2;
  const cellY = (j: number): number => bounds.y + j * 2;

  const visited = new Uint8Array(nx * ny);
  const stack = new Int32Array(nx * ny);
  const dirs = new Int32Array(nx * ny);
  const options = new Int32Array(4);

  let sp = 0;
  const start = ((ny >> 1) * nx + (nx >> 1)) | 0;
  visited[start] = 1;
  grid.set(cellX(start % nx), cellY((start / nx) | 0), Cell.Floor);
  stack[sp] = start;
  dirs[sp] = -1;
  sp++;

  while (sp > 0) {
    const cur = stack[sp - 1]!;
    const ci = cur % nx;
    const cj = (cur / nx) | 0;
    let n = 0;
    for (let d = 0; d < 4; d++) {
      const ni = ci + DX[d]!;
      const nj = cj + DY[d]!;
      if (ni < 0 || nj < 0 || ni >= nx || nj >= ny) continue;
      if (visited[nj * nx + ni]) continue;
      options[n++] = d;
    }
    if (n === 0) {
      sp--;
      continue;
    }
    const prev = dirs[sp - 1]!;
    let chosen = -1;
    if (prev >= 0 && rng.chance(STRAIGHT_BIAS)) {
      for (let k = 0; k < n; k++) if (options[k] === prev) chosen = prev;
    }
    if (chosen < 0) chosen = options[rng.int(0, n - 1)]!;

    const ni = ci + DX[chosen]!;
    const nj = cj + DY[chosen]!;
    grid.set(cellX(ci) + DX[chosen]!, cellY(cj) + DY[chosen]!, Cell.Floor);
    grid.set(cellX(ni), cellY(nj), Cell.Floor);
    visited[nj * nx + ni] = 1;
    stack[sp] = nj * nx + ni;
    dirs[sp] = chosen;
    sp++;
  }

  braid(site, nx, ny, cellX, cellY);
  // Coarse labels go down first so the trunks and chambers carved over the top
  // win the region lookup where they overlap.
  labelWarrens(site, bounds);
  carveTrunks(site, bounds, rng.int(2, 3));
  carveChambers(site, nx, ny, cellX, cellY, rng.int(5, 9));

  // A maze is already illegible, so most of these operations would be lost in
  // it. What lands is the doorway with rock behind it: down here every wall
  // looks the same, and one of them turns out to be a door into the concrete.
  // No columns in openings — a 2.6 m cell leaves too little either side.
  applyWrongness(site, {
    innerRooms: [1, 3],
    doorsToNothing: [10, 18],
    perforatedWalls: [0, 1],
    pillarsInDoorways: [0, 0],
    wallFins: [3, 6],
    tightPillarPatches: [0, 1],
  });

  scatterDamp(site, rng.int(24, 40), 5, 26);
}

/**
 * Open a second wall on some dead ends. A perfect maze is exhausting rather
 * than frightening; a lightly braided one still loses you, but it lets a chase
 * become a chase instead of a corner.
 */
function braid(
  site: GenSite,
  nx: number,
  ny: number,
  cellX: (i: number) => number,
  cellY: (j: number) => number,
): void {
  const { grid, rng } = site;
  const options = new Int32Array(4);
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const x = cellX(i);
      const y = cellY(j);
      if (!grid.isWalkable(x, y)) continue;
      let open = 0;
      let closed = 0;
      for (let d = 0; d < 4; d++) {
        const ni = i + DX[d]!;
        const nj = j + DY[d]!;
        if (ni < 0 || nj < 0 || ni >= nx || nj >= ny) continue;
        if (grid.isWalkable(x + DX[d]!, y + DY[d]!)) open++;
        else options[closed++] = d;
      }
      if (open !== 1 || closed === 0) continue;
      if (!rng.chance(BRAID)) continue;
      const d = options[rng.int(0, closed - 1)]!;
      grid.set(x + DX[d]!, y + DY[d]!, Cell.Floor);
    }
  }
}

/**
 * Two or three straight runs the length of the level. They are still one cell
 * wide — nothing here is generous — but they are the only thing a player can
 * use to work out which way they are facing.
 */
function carveTrunks(site: GenSite, bounds: Rect, count: number): void {
  const { grid, rng } = site;
  for (let i = 0; i < count; i++) {
    const horizontal = rng.chance(0.5);
    const span = horizontal ? bounds.w : bounds.h;
    const crossSpan = horizontal ? bounds.h : bounds.w;
    const crossOrigin = horizontal ? bounds.y : bounds.x;
    // Odd offset keeps the trunk on the maze lattice so it joins corridors
    // rather than running alongside them behind a wall.
    let lane = crossOrigin + rng.int(3, crossSpan - 4);
    if (((lane - crossOrigin) & 1) === 1) lane++;
    const len = Math.floor(span * rng.range(0.7, 0.98));
    const origin = horizontal ? bounds.x : bounds.y;
    const start = origin + rng.int(0, Math.max(0, span - len));
    for (let t = start; t < start + len; t++) {
      if (horizontal) grid.set(t, lane, Cell.Floor);
      else grid.set(lane, t, Cell.Floor);
    }
    addRegion(
      site,
      horizontal ? { x: start, y: lane, w: len, h: 1 } : { x: lane, y: start, w: 1, h: len },
      'hall',
    );
  }
}

/** Rare wide spots. Aligned to the lattice so the corridors already reach them. */
function carveChambers(
  site: GenSite,
  nx: number,
  ny: number,
  cellX: (i: number) => number,
  cellY: (j: number) => number,
  count: number,
): void {
  const { grid, rng } = site;
  const taken: Rect[] = [];
  for (let c = 0; c < count; c++) {
    for (let tries = 0; tries < 24; tries++) {
      const i = rng.int(1, nx - 3);
      const j = rng.int(1, ny - 3);
      const w = rng.int(3, 5);
      const h = rng.int(3, 5);
      const r: Rect = { x: cellX(i), y: cellY(j), w, h };
      if (taken.some((t) => Math.abs(t.x - r.x) < 12 && Math.abs(t.y - r.y) < 12)) continue;
      taken.push(r);
      fillRect(grid, r, Cell.Floor);
      addRegion(site, r, w * h >= 16 ? 'room' : 'closet');
      break;
    }
  }
}

/** Coarse labels so other systems can tell "in the tangle" from "in a chamber". */
function labelWarrens(site: GenSite, bounds: Rect): void {
  const tiles = 4;
  const tw = Math.floor(bounds.w / tiles);
  const th = Math.floor(bounds.h / tiles);
  for (let j = 0; j < tiles; j++) {
    for (let i = 0; i < tiles; i++) {
      addRegion(
        site,
        {
          x: bounds.x + i * tw,
          y: bounds.y + j * th,
          w: i === tiles - 1 ? bounds.w - i * tw : tw,
          h: j === tiles - 1 ? bounds.h - j * th : th,
        },
        'warren',
      );
    }
  }
}
